/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel

import io.peekandpoke.klang.audio_bridge.AdsrCurve
import io.peekandpoke.klang.audio_bridge.IgnitorDsl
import io.peekandpoke.klang.audio_bridge.KatalystDsl
import io.peekandpoke.klang.audio_bridge.KatalystValue
import io.peekandpoke.klang.audio_bridge.SoundValue
import io.peekandpoke.klang.audio_bridge.VoiceData
import io.peekandpoke.klang.audio_bridge.uniqueId

/**
 * Sprudel-specific voice data with flat fields.
 *
 * This is the intermediate representation used within the Sprudel pattern system.
 * It uses flat fields (no complex objects such as an envelope or a filter list) to match
 * the flat value model of cyclic-pattern languages.
 *
 * Gets converted to [VoiceData] when passed to the audio engine.
 *
 * **All properties are `var` by design — for performance.** The pattern engine mutates voice data in
 * place down the modifier chain (via [io.peekandpoke.klang.sprudel.lang.voiceSetter]) instead of
 * allocating a fresh copy per modifier, which is what previously dominated query cost. The trade-off:
 * an instance is NOT safe to share — **the caller is responsible for cloning when a value might be
 * reused or handed to more than one consumer** (use [clone]). The leaf emitters (`AtomicPattern`,
 * `AtomicInfinitePattern`) clone on emission so every queried event owns its
 * data; mutate freely from there. There is intentionally no shared `empty` singleton — construct a
 * fresh one with `SprudelVoiceData()`. See KLANG-00102 (mutable voicedata optimization).
 */
data class SprudelVoiceData(
    // note, scale, freq
    var note: String?,
    var freqHz: Double?,
    var scale: String?,
    /** Chord name (e.g., "Cmaj7", "Dm7", "F/A") for harmonic context */
    var chord: String?,

    // Gain / Dynamics
    var gain: Double?,
    var legato: Double?,
    /**
     * Articulation shorthand: the accents inside a line, multiplied into [gain] at the wire
     * ([toVoiceData]). A sprudel word only, it never crosses to the backend, which knows one
     * level word (signal-flow plan section 6).
     */
    var velocity: Double?,

    // Sound, bank, sound index
    /** Sample bank (e.g. "MPC60" or "AkaiMPC60"), optional.*/
    var bank: String?,
    /**
     * The sound this voice references. Either a [SoundValue.Named] (sample bank entry,
     * pre-registered ignitor, etc., possibly with `name:index` form parsed into [soundIndex])
     * or a [SoundValue.Dsl] inlining an [IgnitorDsl] tree — the latter gets denormalized to
     * a synthetic name at the wire boundary by [toVoiceData].
     */
    var sound: SoundValue?,
    /** Sound index */
    var soundIndex: Int?,

    /**
     * Voice slots: `classic()`'s door slots (with the flat "onepole" [Hz]), the sample's "begin"/"end"/"speed"/"loop",
     * the oscillators' own ("density", "voices", "spread") and raw `ignp()` writes. "panSpread" is written, unread.
     *
     * **Mutable and single-owner, like the `Svd*` groups**, not immutable-replace: a door writes one
     * name in place ([putIgnitorParam]) instead of allocating a fresh bag per slot, which is what keeps a
     * pattern that fills a dozen slots out of the "twenty allocations per note" class the June work
     * removed. [clone] deep-copies it, which is the one allocation per event the design allows, and
     * [toVoiceData] hands the wire a COPY (see there). The contract lives on [ParamBag].
     */
    var ignitorParams: ParamBag?,

    /**
     * The ORBIT's bus slots this event writes, named `<stage>.<knob>` (`"reverb.size"`,
     * `"compressor.ratio"`, `"duck.orbit"`). Same shape and same rules as [ignitorParams], the other
     * host: that bag is the voice's own instrument, this one the chain its orbit runs. Written by
     * `.katp(name, value)` and by the bus doors as aliases (`reverb(...)`, `delay(...)`,
     * `compressor(...)`, `duck(...)` have no voice fields of their own since Katalyst step 5b-3:
     * their slots here are the only place their values live, and their accessors read them back).
     *
     * Mutable and single-owner, and merged the same way as [ignitorParams], last writer wins per name.
     * Carried to `VoiceData.katalystParams` by [toVoiceData].
     */
    var katalystParams: ParamBag?,

    // ADSR amplitude envelope — grouped (see SvdAdsr). Flat fields (attack/decay/…) are accessors below.
    var adsr: SvdAdsr?,

    // Pitch modulation (glide + vibrato) — grouped (see SvdPitchMod). Flat fields are accessors below.
    var pitchMod: SvdPitchMod?,

    // Pitch envelope — grouped (see SvdPitchEnv).
    var pitchEnv: SvdPitchEnv?,

    // FM synthesis — grouped (see SvdFm).
    var fm: SvdFm?,

    // Distortion + lo-fi (coarse / crush) — grouped (see SvdDistortion).
    var distortion: SvdDistortion?,

    // Phaser — grouped (see SvdPhaser).
    var phaser: SvdPhaser?,

    // Tremolo — grouped (see SvdTremolo).
    var tremolo: SvdTremolo?,

    // Filters — grouped (see SvdFilter): cutoff + resonance + optional envelope, one group per filter type.
    // Flat fields (cutoff/hcutoff/bandf/notchf, resonance/…, lp*/hp*/bp*/nf*) are accessors below.
    var lpf: SvdFilter?,
    var hpf: SvdFilter?,
    var bpf: SvdFilter?,
    var notch: SvdFilter?,

    // Routing
    /** The mix channel / bus / orbit / cylinder */
    var cylinder: Int?,

    // Panning (-1.0 = Left, 0.0 = Center, 1.0 = Right)
    var pan: Double?,

    // Sample manipulation — grouped (see SvdSample).
    var sample: SvdSample?,

    // Voice / Singing — grouped (see SvdVowel). Flat fields (vowel/vowelMix/vowelFloor) are accessors below.
    var vowelFx: SvdVowel?,

    // Body resonator — grouped (see SvdBody). Flat fields (body/bodyMix/bodyFloor) are accessors below.
    var bodyFx: SvdBody?,

    // Playback control
    /**
     * Solo amount: 0.0 = no solo, up to 1.0 = full solo; the other patterns play at `1 - amount`. Null = not set.
     * Written by `solo(...)` (`SoloPattern`) on its sounding events and on its control events.
     */
    var solo: Double?,

    /**
     * The id the engine tracks solo state by (`VoiceData.sourceId` on the wire): the atom's source id, replaced by
     * the `solo` call's own id on everything that call solos.
     */
    var patternId: String?,

    /**
     * The output chain (the master) this event switches the playback's bus to, from its start time
     * onward. The same chain type an orbit runs: a [KatalystValue.Named] (a pre-registered custom)
     * or a [KatalystValue.Dsl] inlining a [KatalystDsl]; the latter is denormalized to a synthetic
     * name in [toVoiceData]. Null = no change. A separate field from [katalyst]: the two positions
     * are set by different doors and merge independently.
     */
    var master: KatalystValue?,

    /**
     * The orbit chain this event switches its orbit to, from its start time onward. Either a
     * [KatalystValue.Named] (a pre-registered custom) or a [KatalystValue.Dsl] inlining a
     * [KatalystDsl]; the latter is denormalized to a synthetic name in [toVoiceData]. Null = no
     * change.
     *
     * Like [master], the `.katalyst(...)` door REPLACES whatever chain the pattern already carries
     * (decided 2026-09-18): a chain is one instrument, and `k.classic()` inside the builder is how
     * you start from the familiar one. The field merges last-writer-wins like every other one.
     */
    var katalyst: KatalystValue?,

    /**
     * Control-only event: carries engine state (a [master] or [katalyst] swap, a [solo] keep-alive) and is never
     * synthesized. Set by the top-level `master(...)` / `katalyst(...)` carriers and by `solo(...)` for the
     * events that cover its whole query window; a `note("c3").master(...)` leaves it null so the note still sounds.
     */
    var control: Boolean?,

    // Custom value
    var value: SprudelVoiceValue?,

    /**
     * Semantic tags accumulated via `.tag(...)`. A set by design: tags are unique and carry NO
     * ordering guarantee — consumers must never rely on accumulation order. Copied into engine
     * `VoiceData` by [toVoiceData] (and thus over the wire) for UI subscribers (visualizations)
     * and analysis tools; the synthesis engine ignores them. Treated as immutable-replace, UNLIKE
     * the param maps: a shared reference in [clone] and a fresh set on every write, because a tag
     * is added once per pattern node and never a dozen times per event.
     */
    var tags: Set<String>?,

    /**
     * Tweak names attached in mini-notation (`e3{swell}`) or via `.tweak(...)`, referencing
     * transforms that `tweaks(...)` binds later. A LIST by design, unlike [tags]: tweaks apply in
     * the order written and may repeat, so neither ordering nor duplicates may be dropped.
     *
     * Deliberately NOT copied into engine `VoiceData` by [toVoiceData]: a tweak is a pattern-layer
     * concern that never reaches synthesis, so it would be dead weight on the wire.
     *
     * Treated as immutable-replace like [tags], UNLIKE the param maps (shared reference in
     * [clone], fresh list on write).
     */
    var tweaks: List<String>?,

    /**
     * Silence-culling window in seconds (`cull(seconds)`); `null` = engine default, negative
     * (`noCull()`) = never. Mirrors `VoiceData.cull`, see there for the semantics.
     */
    var cull: Double?,
) {
    // --- Flat-field accessors over the grouped storage -------------------------------------------------
    // Bridge so the rest of the engine/DSL/tests keep using the flat names (data.attack, data.cutoff, …)
    // while storage is grouped. A non-null write lazily creates the group; a null write only clears an
    // existing group (never allocates an empty one). Reads are null-safe through the (possibly null) group.

    private fun adsrOrNew(): SvdAdsr = adsr ?: SvdAdsr().also { adsr = it }
    private fun lpfOrNew(): SvdFilter = lpf ?: SvdFilter().also { lpf = it }
    private fun hpfOrNew(): SvdFilter = hpf ?: SvdFilter().also { hpf = it }
    private fun bpfOrNew(): SvdFilter = bpf ?: SvdFilter().also { bpf = it }
    private fun notchOrNew(): SvdFilter = notch ?: SvdFilter().also { notch = it }
    private fun pitchModOrNew(): SvdPitchMod = pitchMod ?: SvdPitchMod().also { pitchMod = it }
    private fun pitchEnvOrNew(): SvdPitchEnv = pitchEnv ?: SvdPitchEnv().also { pitchEnv = it }
    private fun fmOrNew(): SvdFm = fm ?: SvdFm().also { fm = it }
    private fun distortionOrNew(): SvdDistortion = distortion ?: SvdDistortion().also { distortion = it }
    private fun phaserOrNew(): SvdPhaser = phaser ?: SvdPhaser().also { phaser = it }
    private fun tremoloOrNew(): SvdTremolo = tremolo ?: SvdTremolo().also { tremolo = it }
    private fun sampleOrNew(): SvdSample = sample ?: SvdSample().also { sample = it }
    private fun bodyFxOrNew(): SvdBody = bodyFx ?: SvdBody().also { bodyFx = it }
    private fun vowelFxOrNew(): SvdVowel = vowelFx ?: SvdVowel().also { vowelFx = it }

    // Body resonator — flat accessors over the grouped SvdBody storage.
    var body: String?
        get() = bodyFx?.material
        set(v) {
            if (v != null || bodyFx != null) bodyFxOrNew().material = v
        }
    var bodyMix: Double?
        get() = bodyFx?.mix
        set(v) {
            if (v != null || bodyFx != null) bodyFxOrNew().mix = v
        }
    var bodyFloor: Double?
        get() = bodyFx?.floor
        set(v) {
            if (v != null || bodyFx != null) bodyFxOrNew().floor = v
        }

    // Vowel / formant — flat accessors over the grouped SvdVowel storage.
    var vowel: String?
        get() = vowelFx?.vowel
        set(v) {
            if (v != null || vowelFx != null) vowelFxOrNew().vowel = v
        }
    var vowelMix: Double?
        get() = vowelFx?.mix
        set(v) {
            if (v != null || vowelFx != null) vowelFxOrNew().mix = v
        }
    var vowelFloor: Double?
        get() = vowelFx?.floor
        set(v) {
            if (v != null || vowelFx != null) vowelFxOrNew().floor = v
        }

    var attack: Double?
        get() = adsr?.attack
        set(v) {
            if (v != null || adsr != null) adsrOrNew().attack = v
        }
    var decay: Double?
        get() = adsr?.decay
        set(v) {
            if (v != null || adsr != null) adsrOrNew().decay = v
        }
    var sustain: Double?
        get() = adsr?.sustain
        set(v) {
            if (v != null || adsr != null) adsrOrNew().sustain = v
        }
    var release: Double?
        get() = adsr?.release
        set(v) {
            if (v != null || adsr != null) adsrOrNew().release = v
        }
    var adsrOn: Boolean?
        get() = adsr?.on
        set(v) {
            if (v != null || adsr != null) adsrOrNew().on = v
        }
    var attackCurve: AdsrCurve?
        get() = adsr?.attackCurve
        set(v) {
            if (v != null || adsr != null) adsrOrNew().attackCurve = v
        }
    var decayCurve: AdsrCurve?
        get() = adsr?.decayCurve
        set(v) {
            if (v != null || adsr != null) adsrOrNew().decayCurve = v
        }
    var releaseCurve: AdsrCurve?
        get() = adsr?.releaseCurve
        set(v) {
            if (v != null || adsr != null) adsrOrNew().releaseCurve = v
        }

    var cutoff: Double?
        get() = lpf?.cutoff
        set(v) {
            if (v != null || lpf != null) lpfOrNew().cutoff = v
        }
    var resonance: Double?
        get() = lpf?.resonance
        set(v) {
            if (v != null || lpf != null) lpfOrNew().resonance = v
        }
    var lpattack: Double?
        get() = lpf?.attack
        set(v) {
            if (v != null || lpf != null) lpfOrNew().attack = v
        }
    var lpdecay: Double?
        get() = lpf?.decay
        set(v) {
            if (v != null || lpf != null) lpfOrNew().decay = v
        }
    var lpsustain: Double?
        get() = lpf?.sustain
        set(v) {
            if (v != null || lpf != null) lpfOrNew().sustain = v
        }
    var lprelease: Double?
        get() = lpf?.release
        set(v) {
            if (v != null || lpf != null) lpfOrNew().release = v
        }
    var lpenv: Double?
        get() = lpf?.env
        set(v) {
            if (v != null || lpf != null) lpfOrNew().env = v
        }
    var lpPasses: Double?
        get() = lpf?.passes
        set(v) {
            if (v != null || lpf != null) lpfOrNew().passes = v
        }
    var lpAttackCurve: AdsrCurve?
        get() = lpf?.attackCurve
        set(v) {
            if (v != null || lpf != null) lpfOrNew().attackCurve = v
        }
    var lpDecayCurve: AdsrCurve?
        get() = lpf?.decayCurve
        set(v) {
            if (v != null || lpf != null) lpfOrNew().decayCurve = v
        }
    var lpReleaseCurve: AdsrCurve?
        get() = lpf?.releaseCurve
        set(v) {
            if (v != null || lpf != null) lpfOrNew().releaseCurve = v
        }

    var hcutoff: Double?
        get() = hpf?.cutoff
        set(v) {
            if (v != null || hpf != null) hpfOrNew().cutoff = v
        }
    var hresonance: Double?
        get() = hpf?.resonance
        set(v) {
            if (v != null || hpf != null) hpfOrNew().resonance = v
        }
    var hpPasses: Double?
        get() = hpf?.passes
        set(v) {
            if (v != null || hpf != null) hpfOrNew().passes = v
        }
    var hpattack: Double?
        get() = hpf?.attack
        set(v) {
            if (v != null || hpf != null) hpfOrNew().attack = v
        }
    var hpdecay: Double?
        get() = hpf?.decay
        set(v) {
            if (v != null || hpf != null) hpfOrNew().decay = v
        }
    var hpsustain: Double?
        get() = hpf?.sustain
        set(v) {
            if (v != null || hpf != null) hpfOrNew().sustain = v
        }
    var hprelease: Double?
        get() = hpf?.release
        set(v) {
            if (v != null || hpf != null) hpfOrNew().release = v
        }
    var hpenv: Double?
        get() = hpf?.env
        set(v) {
            if (v != null || hpf != null) hpfOrNew().env = v
        }
    var hpAttackCurve: AdsrCurve?
        get() = hpf?.attackCurve
        set(v) {
            if (v != null || hpf != null) hpfOrNew().attackCurve = v
        }
    var hpDecayCurve: AdsrCurve?
        get() = hpf?.decayCurve
        set(v) {
            if (v != null || hpf != null) hpfOrNew().decayCurve = v
        }
    var hpReleaseCurve: AdsrCurve?
        get() = hpf?.releaseCurve
        set(v) {
            if (v != null || hpf != null) hpfOrNew().releaseCurve = v
        }

    var bandf: Double?
        get() = bpf?.cutoff
        set(v) {
            if (v != null || bpf != null) bpfOrNew().cutoff = v
        }
    var bandq: Double?
        get() = bpf?.resonance
        set(v) {
            if (v != null || bpf != null) bpfOrNew().resonance = v
        }
    var bpattack: Double?
        get() = bpf?.attack
        set(v) {
            if (v != null || bpf != null) bpfOrNew().attack = v
        }
    var bpdecay: Double?
        get() = bpf?.decay
        set(v) {
            if (v != null || bpf != null) bpfOrNew().decay = v
        }
    var bpsustain: Double?
        get() = bpf?.sustain
        set(v) {
            if (v != null || bpf != null) bpfOrNew().sustain = v
        }
    var bprelease: Double?
        get() = bpf?.release
        set(v) {
            if (v != null || bpf != null) bpfOrNew().release = v
        }
    var bpenv: Double?
        get() = bpf?.env
        set(v) {
            if (v != null || bpf != null) bpfOrNew().env = v
        }
    var bpAttackCurve: AdsrCurve?
        get() = bpf?.attackCurve
        set(v) {
            if (v != null || bpf != null) bpfOrNew().attackCurve = v
        }
    var bpDecayCurve: AdsrCurve?
        get() = bpf?.decayCurve
        set(v) {
            if (v != null || bpf != null) bpfOrNew().decayCurve = v
        }
    var bpReleaseCurve: AdsrCurve?
        get() = bpf?.releaseCurve
        set(v) {
            if (v != null || bpf != null) bpfOrNew().releaseCurve = v
        }

    var notchf: Double?
        get() = notch?.cutoff
        set(v) {
            if (v != null || notch != null) notchOrNew().cutoff = v
        }
    var nresonance: Double?
        get() = notch?.resonance
        set(v) {
            if (v != null || notch != null) notchOrNew().resonance = v
        }
    var nfattack: Double?
        get() = notch?.attack
        set(v) {
            if (v != null || notch != null) notchOrNew().attack = v
        }
    var nfdecay: Double?
        get() = notch?.decay
        set(v) {
            if (v != null || notch != null) notchOrNew().decay = v
        }
    var nfsustain: Double?
        get() = notch?.sustain
        set(v) {
            if (v != null || notch != null) notchOrNew().sustain = v
        }
    var nfrelease: Double?
        get() = notch?.release
        set(v) {
            if (v != null || notch != null) notchOrNew().release = v
        }
    var nfenv: Double?
        get() = notch?.env
        set(v) {
            if (v != null || notch != null) notchOrNew().env = v
        }
    var nfAttackCurve: AdsrCurve?
        get() = notch?.attackCurve
        set(v) {
            if (v != null || notch != null) notchOrNew().attackCurve = v
        }
    var nfDecayCurve: AdsrCurve?
        get() = notch?.decayCurve
        set(v) {
            if (v != null || notch != null) notchOrNew().decayCurve = v
        }
    var nfReleaseCurve: AdsrCurve?
        get() = notch?.releaseCurve
        set(v) {
            if (v != null || notch != null) notchOrNew().releaseCurve = v
        }

    var accelerate: Double?
        get() = pitchMod?.accelerate
        set(v) {
            if (v != null || pitchMod != null) pitchModOrNew().accelerate = v
        }
    var vibrato: Double?
        get() = pitchMod?.vibrato
        set(v) {
            if (v != null || pitchMod != null) pitchModOrNew().vibrato = v
        }
    var vibratoMod: Double?
        get() = pitchMod?.vibratoMod
        set(v) {
            if (v != null || pitchMod != null) pitchModOrNew().vibratoMod = v
        }
    var vibratoRangeFrom: Double?
        get() = pitchMod?.vibratoRangeFrom
        set(v) {
            if (v != null || pitchMod != null) pitchModOrNew().vibratoRangeFrom = v
        }
    var vibratoRangeTo: Double?
        get() = pitchMod?.vibratoRangeTo
        set(v) {
            if (v != null || pitchMod != null) pitchModOrNew().vibratoRangeTo = v
        }
    var vibratoPhase: Double?
        get() = pitchMod?.vibratoPhase
        set(v) {
            if (v != null || pitchMod != null) pitchModOrNew().vibratoPhase = v
        }

    var pAttack: Double?
        get() = pitchEnv?.pAttack
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pAttack = v
        }
    var pDecay: Double?
        get() = pitchEnv?.pDecay
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pDecay = v
        }
    var pSustain: Double?
        get() = pitchEnv?.pSustain
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pSustain = v
        }
    var pRelease: Double?
        get() = pitchEnv?.pRelease
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pRelease = v
        }
    var pEnv: Double?
        get() = pitchEnv?.pEnv
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pEnv = v
        }
    var pAttackCurve: AdsrCurve?
        get() = pitchEnv?.pAttackCurve
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pAttackCurve = v
        }
    var pDecayCurve: AdsrCurve?
        get() = pitchEnv?.pDecayCurve
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pDecayCurve = v
        }
    var pReleaseCurve: AdsrCurve?
        get() = pitchEnv?.pReleaseCurve
        set(v) {
            if (v != null || pitchEnv != null) pitchEnvOrNew().pReleaseCurve = v
        }

    var fmh: Double?
        get() = fm?.fmh
        set(v) {
            if (v != null || fm != null) fmOrNew().fmh = v
        }
    var fmAttack: Double?
        get() = fm?.fmAttack
        set(v) {
            if (v != null || fm != null) fmOrNew().fmAttack = v
        }
    var fmDecay: Double?
        get() = fm?.fmDecay
        set(v) {
            if (v != null || fm != null) fmOrNew().fmDecay = v
        }
    var fmSustain: Double?
        get() = fm?.fmSustain
        set(v) {
            if (v != null || fm != null) fmOrNew().fmSustain = v
        }
    var fmEnv: Double?
        get() = fm?.fmEnv
        set(v) {
            if (v != null || fm != null) fmOrNew().fmEnv = v
        }
    var fmRelease: Double?
        get() = fm?.fmRelease
        set(v) {
            if (v != null || fm != null) fmOrNew().fmRelease = v
        }

    var distort: Double?
        get() = distortion?.distort
        set(v) {
            if (v != null || distortion != null) distortionOrNew().distort = v
        }
    var distortShape: String?
        get() = distortion?.distortShape
        set(v) {
            if (v != null || distortion != null) distortionOrNew().distortShape = v
        }
    var coarse: Double?
        get() = distortion?.coarse
        set(v) {
            if (v != null || distortion != null) distortionOrNew().coarse = v
        }
    var crush: Double?
        get() = distortion?.crush
        set(v) {
            if (v != null || distortion != null) distortionOrNew().crush = v
        }

    var phaserRate: Double?
        get() = phaser?.phaserRate
        set(v) {
            if (v != null || phaser != null) phaserOrNew().phaserRate = v
        }
    var phaserDepth: Double?
        get() = phaser?.phaserDepth
        set(v) {
            if (v != null || phaser != null) phaserOrNew().phaserDepth = v
        }
    var phaserCenter: Double?
        get() = phaser?.phaserCenter
        set(v) {
            if (v != null || phaser != null) phaserOrNew().phaserCenter = v
        }
    var phaserSweep: Double?
        get() = phaser?.phaserSweep
        set(v) {
            if (v != null || phaser != null) phaserOrNew().phaserSweep = v
        }

    var phaserFloor: Double?
        get() = phaser?.phaserFloor
        set(v) {
            if (v != null || phaser != null) phaserOrNew().phaserFloor = v
        }

    var tremoloRate: Double?
        get() = tremolo?.tremoloRate
        set(v) {
            if (v != null || tremolo != null) tremoloOrNew().tremoloRate = v
        }
    var tremoloDepth: Double?
        get() = tremolo?.tremoloDepth
        set(v) {
            if (v != null || tremolo != null) tremoloOrNew().tremoloDepth = v
        }
    var tremoloShape: String?
        get() = tremolo?.tremoloShape
        set(v) {
            if (v != null || tremolo != null) tremoloOrNew().tremoloShape = v
        }

    var begin: Double?
        get() = sample?.begin
        set(v) {
            if (v != null || sample != null) sampleOrNew().begin = v
        }
    var end: Double?
        get() = sample?.end
        set(v) {
            if (v != null || sample != null) sampleOrNew().end = v
        }
    var speed: Double?
        get() = sample?.speed
        set(v) {
            if (v != null || sample != null) sampleOrNew().speed = v
        }
    var unit: String?
        get() = sample?.unit
        set(v) {
            if (v != null || sample != null) sampleOrNew().unit = v
        }
    var loop: Boolean?
        get() = sample?.loop
        set(v) {
            if (v != null || sample != null) sampleOrNew().loop = v
        }
    var cut: Int?
        get() = sample?.cut
        set(v) {
            if (v != null || sample != null) sampleOrNew().cut = v
        }
    // --------------------------------------------------------------------------------------------------

    /**
     * Fresh deep-enough copy: the flat core fields are copied shallow (immutable scalars), and each
     * non-null group is `copy()`-ed so the clone owns its own groups (single-owner invariant — see the
     * leaf emitters `AtomicPattern`/`AtomicInfinitePattern`). `ignitorParams` and `katalystParams` are
     * mutable [ParamBag]s and are copied here for the same reason the groups are: the clone owns them, so
     * a door can write a name in place instead of allocating a bag per slot. As more clusters become groups,
     * add them to the deep-copy list here.
     */
    fun clone(): SprudelVoiceData = copy(
        ignitorParams = ignitorParams?.copy(),
        katalystParams = katalystParams?.copy(),
        adsr = adsr?.copy(),
        lpf = lpf?.copy(),
        hpf = hpf?.copy(),
        bpf = bpf?.copy(),
        notch = notch?.copy(),
        pitchMod = pitchMod?.copy(),
        pitchEnv = pitchEnv?.copy(),
        fm = fm?.copy(),
        distortion = distortion?.copy(),
        phaser = phaser?.copy(),
        tremolo = tremolo?.copy(),
        sample = sample?.copy(),
        bodyFx = bodyFx?.copy(),
        vowelFx = vowelFx?.copy(),
    )

    fun merge(other: SprudelVoiceData): SprudelVoiceData {
        return SprudelVoiceData(
            note = other.note ?: note,
            freqHz = other.freqHz ?: freqHz,
            scale = other.scale ?: scale,
            chord = other.chord ?: chord,
            gain = other.gain ?: gain,
            legato = other.legato ?: legato,
            velocity = other.velocity ?: velocity,
            bank = other.bank ?: bank,
            sound = other.sound ?: sound,
            soundIndex = other.soundIndex ?: soundIndex,
            ignitorParams = mergeParamBag(ignitorParams, other.ignitorParams),
            katalystParams = mergeParamBag(katalystParams, other.katalystParams),
            adsr = mergeSvdAdsr(adsr, other.adsr),
            pitchMod = mergeSvdPitchMod(pitchMod, other.pitchMod),
            pitchEnv = mergeSvdPitchEnv(pitchEnv, other.pitchEnv),
            fm = mergeSvdFm(fm, other.fm),
            distortion = mergeSvdDistortion(distortion, other.distortion),
            phaser = mergeSvdPhaser(phaser, other.phaser),
            tremolo = mergeSvdTremolo(tremolo, other.tremolo),
            lpf = mergeSvdFilter(lpf, other.lpf),
            hpf = mergeSvdFilter(hpf, other.hpf),
            bpf = mergeSvdFilter(bpf, other.bpf),
            notch = mergeSvdFilter(notch, other.notch),
            cylinder = other.cylinder ?: cylinder,
            pan = other.pan ?: pan,
            sample = mergeSvdSample(sample, other.sample),
            vowelFx = mergeSvdVowel(vowelFx, other.vowelFx),
            bodyFx = mergeSvdBody(bodyFx, other.bodyFx),
            solo = other.solo ?: solo,
            patternId = patternId,  // Never merge - preserve original source ID
            master = other.master ?: master,
            katalyst = other.katalyst ?: katalyst,
            // control is NOT merged (like patternId): it says "this event makes no sound", which is
            // a property of the carrier itself. Taking it from `other` would let a merged-in master
            // carrier silence real notes.
            control = control,
            value = other.value ?: value,
            tags = mergeTags(tags, other.tags),
            tweaks = mergeTweaks(tweaks, other.tweaks),
            cull = other.cull ?: cull,
        )
    }

    /**
     * In-place counterpart of [merge]: folds [other]'s non-null fields into this instance (other wins),
     * mutating it rather than allocating. `patternId` is preserved (never taken from other), matching
     * [merge]. Only safe on a single-owner instance (see [clone]). Guarded against drift from [merge] by
     * `SprudelVoiceDataSpec`.
     */
    fun mergeFrom(other: SprudelVoiceData) {
        note = other.note ?: note
        freqHz = other.freqHz ?: freqHz
        scale = other.scale ?: scale
        chord = other.chord ?: chord
        gain = other.gain ?: gain
        legato = other.legato ?: legato
        velocity = other.velocity ?: velocity
        bank = other.bank ?: bank
        sound = other.sound ?: sound
        soundIndex = other.soundIndex ?: soundIndex
        ignitorParams = mergeParamBagInto(ignitorParams, other.ignitorParams)
        katalystParams = mergeParamBagInto(katalystParams, other.katalystParams)
        adsr = mergeSvdAdsr(adsr, other.adsr)
        pitchMod = mergeSvdPitchMod(pitchMod, other.pitchMod)
        pitchEnv = mergeSvdPitchEnv(pitchEnv, other.pitchEnv)
        fm = mergeSvdFm(fm, other.fm)
        distortion = mergeSvdDistortion(distortion, other.distortion)
        phaser = mergeSvdPhaser(phaser, other.phaser)
        tremolo = mergeSvdTremolo(tremolo, other.tremolo)
        lpf = mergeSvdFilter(lpf, other.lpf)
        hpf = mergeSvdFilter(hpf, other.hpf)
        bpf = mergeSvdFilter(bpf, other.bpf)
        notch = mergeSvdFilter(notch, other.notch)
        cylinder = other.cylinder ?: cylinder
        pan = other.pan ?: pan
        sample = mergeSvdSample(sample, other.sample)
        vowelFx = mergeSvdVowel(vowelFx, other.vowelFx)
        bodyFx = mergeSvdBody(bodyFx, other.bodyFx)
        solo = other.solo ?: solo
        // patternId intentionally preserved (never taken from other) — matches merge()
        master = other.master ?: master
        katalyst = other.katalyst ?: katalyst
        // control intentionally NOT merged — see merge()
        value = other.value ?: value
        tags = mergeTags(tags, other.tags)
        tweaks = mergeTweaks(tweaks, other.tweaks)
        cull = other.cull ?: cull
    }

    fun isTruthy(): Boolean {
        val noteStr = note ?: ""
        // "0" and "false" strings are false, "~" is false (but usually filtered out before)
        val noteTruthy = noteStr.isNotEmpty() && noteStr != "~" && noteStr != "0" && noteStr != "false"

        val valueTruthy = value?.isTruthy() ?: false

        return valueTruthy || noteTruthy
    }

    fun isNotTruthy(): Boolean {
        return !isTruthy()
    }

    /**
     * The wire's one level word: [gain] with [velocity] multiplied into it.
     *
     * `velocity` is sprudel's articulation shorthand and stops here (signal-flow plan section 6).
     * Both unset stays unset, so the wire stays sparse and the engine's own `?: 1.0` answers; any
     * other combination is `(gain ?: 1.0) * (velocity ?: 1.0)`, the operands and the order the
     * backend used to compute in the voice factory, so an unaccented voice keeps its bits.
     *
     * A non-finite value reads as UNSET, PER OPERAND and before the product (`/dsl-design` §4).
     * Substituting after the product instead would turn `gain(0.5).velocity(NaN)` into a NaN the
     * engine reads as 1.0, i.e. FULL level where the author asked for half, louder than anything
     * they wrote. Per operand, that call is `0.5`. Two operands that both read as unset give
     * `null`, the same answer as writing neither, so the wire stays sparse. A product that
     * OVERFLOWS to infinity is left alone here and caught by the engine's own guard.
     *
     * Folded HERE and not at the `velocity()` door: `velocity(p)` read as `gain(mul(p))` does
     * nothing on an event whose gain is unset (a mapper on an unset field is a no-op, by design)
     * and would make `.velocity(0.7).gain(0.5)` order-dependent.
     */
    private fun foldedGain(): Double? {
        // NaN-guard: per operand, before the product (see the KDoc for why the order matters)
        val g = gain?.takeIf { it.isFinite() }
        val v = velocity?.takeIf { it.isFinite() }

        if (g == null && v == null) {
            return null
        }

        return (g ?: 1.0) * (v ?: 1.0)
    }

    /**
     * Converts this Sprudel-specific voice data to audio engine [VoiceData].
     *
     * The voice doors' typed fields (the envelope, the four filters, crush, coarse, distort, tremolo and the
     * sample's begin, end, speed and loop) travel as SLOT KEYS in `ignitorParams`, the names the instruments read
     * ([classicSlotParams], phase 3 step 8). The orbit stages (vowel, body, phaser and the rest) travel as
     * `katalystParams` slots. The typed wire fields of all of them left `VoiceData` in phase 3 step 9.
     *
     * For inline ignitors ([SoundValue.Dsl]) the wire-level `sound` name is resolved via
     * the process-wide [uniqueId] map — playbacks are expected to pre-register inline
     * ignitors with their backend so that name is already known to the runtime by the
     * time voice events referencing it are scheduled.
     */
    fun toVoiceData(): VoiceData {
        val soundName: String? = when (val s = sound) {
            null -> null
            is SoundValue.Named -> s.name
            is SoundValue.Dsl -> s.ignitor.uniqueId()
        }

        // Same denormalization for the two chain references, the output's and the orbit's.
        // `.name` is the memoized `uniqueId()` of the chain: one structural hash per value
        // instance rather than one per event (see KatalystValue.Dsl.name).
        val masterName: String? = when (val m = master) {
            null -> null
            is KatalystValue.Named -> m.name
            is KatalystValue.Dsl -> m.name
        }

        val katalystName: String? = when (val k = katalyst) {
            null -> null
            is KatalystValue.Named -> k.name
            is KatalystValue.Dsl -> k.name
        }

        return VoiceData(
            note = note,
            freqHz = freqHz,
            gain = foldedGain(),
            legato = legato,
            bank = bank,
            sound = soundName,
            soundIndex = soundIndex,
            // A COPY, not the event's own bag: the wire value outlives the pattern event. The
            // backend holds `Voice.katalystParams` for the whole life of the voice and its chain
            // gates the re-resolve on the map's IDENTITY, so a map sprudel could still write into
            // would change an orbit's settings invisibly. `ignitorParams` follows the same rule, one
            // contract for both (`ParamBag.toKlangMap`). The boundary already allocates a `VoiceData`,
            // and one copy here replaces the one-per-slot copies the doors used to make.
            // The voice doors travel as slot keys in this bag (`classicSlotParams`, phase 3 step 8); their typed
            // wire fields left in phase 3 step 9, the pitch envelope's in pitch pipeline step 1, the vibrato's in step 2,
            // `accelerate` in step 3, the FM's in step 4.
            ignitorParams = classicSlotParams(),
            katalystParams = katalystParams?.toKlangMap(),
            cylinder = cylinder,
            pan = pan,
            cut = cut,
            solo = solo,
            sourceId = patternId,
            master = masterName,
            katalyst = katalystName,
            control = control,
            tags = tags,
            cull = cull,
        )
    }
}

/**
 * Shared all-null template, cloned by [createSprudelVoiceData]. `@PublishedApi internal` so the inline
 * factory can reference it across the module. Never mutate it directly — it is only ever `clone()`d.
 * This full-field constructor call (plus `merge()`/`mergeFrom`) is the compile-time guard that no field
 * is forgotten, since the primary constructor has no defaults.
 */
@PublishedApi
internal val blueprint = SprudelVoiceData(
    note = null,
    freqHz = null,
    scale = null,
    chord = null,
    gain = null,
    legato = null,
    velocity = null,
    bank = null,
    sound = null,
    soundIndex = null,
    ignitorParams = null,
    katalystParams = null,
    adsr = null,
    pitchMod = null,
    pitchEnv = null,
    fm = null,
    distortion = null,
    phaser = null,
    tremolo = null,
    lpf = null,
    hpf = null,
    bpf = null,
    notch = null,
    cylinder = null,
    pan = null,
    sample = null,
    vowelFx = null,
    bodyFx = null,
    solo = null,
    patternId = null,
    master = null,
    katalyst = null,
    control = null,
    value = null,
    tags = null,
    tweaks = null,
    cull = null,
)

/**
 * Factory for a fresh [SprudelVoiceData], configured via [config].
 *
 * Clones the all-null [blueprint] and applies [config] to set the fields you want — avoids threading
 * 100+ constructor params through every call site. `inline`, so `config` is inlined (no lambda
 * allocation): `createSprudelVoiceData { gain = 0.5 }` compiles to `blueprint.clone().also { it.gain = 0.5 }`.
 *
 * Note: inside [config] the receiver is the new instance, so a bare name on the right-hand side resolves
 * to the instance's property, not an outer local — write `also { it.field = localValue }` when the local
 * shares a field's name (e.g. `value`).
 */
inline fun createSprudelVoiceData(config: SprudelVoiceData.() -> Unit = {}): SprudelVoiceData =
    blueprint.clone().apply(config)

/**
 * Merges two tag sets (union). Null when both are null — a merge must not materialize an empty
 * set on untagged data.
 */
private fun mergeTags(
    base: Set<String>?,
    other: Set<String>?,
): Set<String>? = when {
    base == null -> other
    other == null -> base
    else -> base + other
}

/**
 * In-place tag add: no-op if [tag] is already present, else assigns a fresh set with [tag] added.
 * `tags` is treated as immutable-replace, unlike the param maps, so a fresh set is assigned to the
 * field and no new [SprudelVoiceData] is allocated. Only safe on a single-owner instance (see [clone]).
 */
fun SprudelVoiceData.addTag(tag: String) {
    val current = tags
    if (current != null && tag in current) return
    tags = current.orEmpty() + tag
}

/** Copy counterpart of [addTag]: returns this unchanged when [tag] is already present. */
fun SprudelVoiceData.withTag(tag: String): SprudelVoiceData {
    val current = tags
    if (current != null && tag in current) return this
    return copy(tags = current.orEmpty() + tag)
}

/**
 * Concatenates two tweak lists: [base] first, then [other]. Null when both are null — a merge must
 * not materialize an empty list on untweaked data.
 *
 * Concatenation, not union: order is significant and repeats are meaningful. [other] is the later
 * (outer) modifier in every [SprudelVoiceData.merge] direction, so inner tweaks stay in front.
 */
private fun mergeTweaks(
    base: List<String>?,
    other: List<String>?,
): List<String>? = when {
    base == null -> other
    other == null -> base
    else -> base + other
}

/**
 * In-place tweak append. Unlike [addTag] this is NOT idempotent: a repeated tweak applies twice,
 * which is the whole point of `tweaks` being a list. `tweaks` is treated as immutable-replace,
 * unlike the param maps, so a fresh list is assigned to the field and no new [SprudelVoiceData] is allocated. Only safe on a single-owner instance
 * (see [SprudelVoiceData.clone]).
 */
fun SprudelVoiceData.addTweak(tweak: String) {
    tweaks = tweaks.orEmpty() + tweak
}

/** Copy counterpart of [addTweak]. Always allocates: appending is never a no-op. */
fun SprudelVoiceData.withTweak(tweak: String): SprudelVoiceData =
    copy(tweaks = tweaks.orEmpty() + tweak)

/** Appends several tweaks in one copy, preserving [names]' order. Returns this when [names] is empty. */
fun SprudelVoiceData.withTweaks(names: List<String>): SprudelVoiceData = when {
    names.isEmpty() -> this
    else -> copy(tweaks = tweaks.orEmpty() + names)
}

/**
 * Merges two param bags (`ignitorParams`, `katalystParams`) into a FRESH one: other's values override
 * this's values, name by name. One function for both, because the two bags differ in their HOST,
 * not in their shape.
 *
 * Always a new bag, never an alias, exactly as `mergeSvdAdsr` and its siblings always return a new
 * group: [SprudelVoiceData.merge] builds a new instance, and handing it either operand's mutable
 * bag would make two owners of one bag. The in-place twin is [mergeParamBagInto].
 */
private fun mergeParamBag(base: ParamBag?, other: ParamBag?): ParamBag? = when {
    base == null -> other?.copy()
    other == null -> base.copy()
    else -> base.copy().also { it.mergeFrom(other) }
}

/**
 * In-place counterpart of [mergeParamBag], for [SprudelVoiceData.mergeFrom]: [other]'s entries go
 * into the receiver's own bag, which is the whole point of the bag being mutable. Only when the
 * receiver has none is a bag allocated, and then it is a copy: [other] belongs to another voice.
 */
private fun mergeParamBagInto(target: ParamBag?, other: ParamBag?): ParamBag? {
    if (other == null) {
        return target
    }

    if (target == null) {
        return other.copy()
    }

    target.mergeFrom(other)

    return target
}

/**
 * The voice's own [ParamBag], allocated on first use.
 *
 * The one door to the bag for a writer that is going to write: a stage fill reaches for it once and
 * then calls [ParamBag.set] / [ParamBag.setOrDefault] per slot, so a door that fills five knobs
 * allocates at most one bag. Only safe on a single-owner instance (see [SprudelVoiceData.clone]).
 */
fun SprudelVoiceData.ignitorParamsOrNew(): ParamBag = ignitorParams ?: ParamBag().also { ignitorParams = it }

/** The orbit chain's [ParamBag], the [ignitorParamsOrNew] twin on the other host. */
fun SprudelVoiceData.katalystParamsOrNew(): ParamBag = katalystParams ?: ParamBag().also { katalystParams = it }

/**
 * Writes ONE Ignitor slot on this instance, in place, and nothing at all when [value] is null: a door
 * that wrote nothing on this event must not leave an empty bag behind, because an empty bag is not
 * the same wire value as none at all.
 */
fun SprudelVoiceData.putIgnitorParam(name: String, value: Double?) {
    if (value == null) return

    ignitorParamsOrNew().set(name, value)
}

/**
 * In-place write of ONE orbit-chain slot, the [putIgnitorParam] twin on the other host (null is a
 * no-op for the same reason).
 *
 * ONE name into the bag this instance already owns. A door that fills a whole STAGE takes
 * [katalystParamsOrNew] instead and writes the slots on the bag, through the fill rule's one
 * method ([ParamBag.setOrDefault]). Only safe on a single-owner instance (see
 * [SprudelVoiceData.clone]).
 */
fun SprudelVoiceData.putKatalystParam(name: String, value: Double?) {
    if (value == null) return

    katalystParamsOrNew().set(name, value)
}

/**
 * Convenience accessor that extracts the [SoundValue.Named.name] from [SprudelVoiceData.sound],
 * or null if sound is null or a [SoundValue.Dsl].
 */
val SprudelVoiceData.soundName: String? get() = (sound as? SoundValue.Named)?.name
